import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LoadingContainer, ButtonSpinner } from '../components/ui/Spinner';
import {
    MapPinIcon,
    PhoneIcon,
    GlobeAltIcon,
    PencilIcon,
    CheckIcon,
    XMarkIcon,
    ArrowLeftIcon,
} from '@heroicons/react/24/outline';

// Game quality options
const GAME_QUALITY_OPTIONS = [
    { value: '', label: 'Not Set' },
    { value: 'excellent', label: 'Excellent' },
    { value: 'good', label: 'Good' },
    { value: 'average', label: 'Average' },
    { value: 'tough', label: 'Tough' },
    { value: 'very_tough', label: 'Very Tough' },
];

const GAME_QUALITY_BADGE_COLORS = {
    excellent: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    good: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    average: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    tough: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    very_tough: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

// Player category colors
const PLAYER_CATEGORY_COLORS = {
    shark: 'bg-gray-900 dark:bg-gray-700',
    reg: 'bg-red-500',
    fish: 'bg-green-500',
    whale: 'bg-pink-500',
    nit: 'bg-yellow-500',
    unknown: 'bg-gray-400',
};

// Stat Card Component
const StatCard = ({ label, value, subValue, colorClass = '' }) => (
    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{label}</p>
        <p className={`text-2xl font-bold ${colorClass || 'text-gray-900 dark:text-white'}`}>
            {value}
        </p>
        {subValue && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{subValue}</p>
        )}
    </div>
);

// Editable Field Component
const EditableField = ({ label, value, onChange, type = 'text', options = null, textarea = false }) => {
    const [editing, setEditing] = useState(false);
    const [tempValue, setTempValue] = useState(value || '');

    useEffect(() => {
        setTempValue(value || '');
    }, [value]);

    const handleSave = () => {
        onChange(tempValue);
        setEditing(false);
    };

    const handleCancel = () => {
        setTempValue(value || '');
        setEditing(false);
    };

    if (editing) {
        return (
            <div className="flex items-start gap-2">
                {options ? (
                    <select
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600
                                 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white
                                 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    >
                        {options.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                ) : textarea ? (
                    <textarea
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        rows={3}
                        className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600
                                 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white
                                 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                ) : (
                    <input
                        type={type}
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600
                                 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white
                                 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                )}
                <button
                    onClick={handleSave}
                    className="p-2 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg"
                >
                    <CheckIcon className="h-5 w-5" />
                </button>
                <button
                    onClick={handleCancel}
                    className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg"
                >
                    <XMarkIcon className="h-5 w-5" />
                </button>
            </div>
        );
    }

    const displayValue = options
        ? options.find(o => o.value === value)?.label || 'Not Set'
        : value || '-';

    return (
        <div
            onClick={() => setEditing(true)}
            className="flex items-start justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30
                     rounded-lg p-2 -m-2 group transition-colors"
        >
            <div className="flex-1">
                <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
                <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{displayValue}</p>
            </div>
            <PencilIcon className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
        </div>
    );
};

function LocationDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [location, setLocation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    const fetchLocation = () => {
        setLoading(true);
        axios.get(`/api/locations/${id}/`)
            .then(res => {
                setLocation(res.data);
                setLoading(false);
            })
            .catch(err => {
                setError(err.response?.data?.detail || 'Failed to fetch location.');
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchLocation();
    }, [id]);

    const updateField = (field, value) => {
        setSaving(true);
        axios.patch(`/api/locations/${id}/`, { [field]: value })
            .then(res => {
                setLocation(res.data);
            })
            .catch(err => {
                console.error('Failed to update:', err);
            })
            .finally(() => {
                setSaving(false);
            });
    };

    if (loading) {
        return <LoadingContainer message="Loading location..." />;
    }

    if (error) {
        return (
            <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/30
                          text-red-700 dark:text-red-400 m-4">
                {error}
            </div>
        );
    }

    if (!location) {
        return null;
    }

    const profitColor = location.total_profit >= 0
        ? 'text-green-600 dark:text-green-400'
        : 'text-red-600 dark:text-red-400';

    const hourlyColor = location.hourly_rate >= 0
        ? 'text-green-600 dark:text-green-400'
        : 'text-red-600 dark:text-red-400';

    return (
        <div className="max-w-7xl mx-auto mt-8 mb-8 px-2 sm:px-4">
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <button
                    onClick={() => navigate('/locations')}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                    <ArrowLeftIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                </button>
                <div className="flex-1">
                    <EditableField
                        label=""
                        value={location.name}
                        onChange={(val) => updateField('name', val)}
                    />
                </div>
                {saving && <ButtonSpinner />}
            </div>

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Info */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Stats Cards */}
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Statistics</h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                            <StatCard
                                label="Sessions"
                                value={location.session_count || 0}
                            />
                            <StatCard
                                label="Hours"
                                value={location.total_hours || 0}
                            />
                            <StatCard
                                label="Total Profit"
                                value={`$${(location.total_profit || 0).toLocaleString()}`}
                                colorClass={profitColor}
                            />
                            <StatCard
                                label="Hourly Rate"
                                value={`$${(location.hourly_rate || 0).toFixed(2)}`}
                                colorClass={hourlyColor}
                            />
                            <StatCard
                                label="Win Rate"
                                value={`${location.win_rate || 0}%`}
                                subValue={location.last_session_date ? `Last: ${location.last_session_date}` : null}
                            />
                        </div>
                    </div>

                    {/* Location Details */}
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Location Details</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <EditableField
                                label="Address"
                                value={location.address}
                                onChange={(val) => updateField('address', val)}
                            />
                            <div className="grid grid-cols-2 gap-4">
                                <EditableField
                                    label="City"
                                    value={location.city}
                                    onChange={(val) => updateField('city', val)}
                                />
                                <EditableField
                                    label="State"
                                    value={location.state}
                                    onChange={(val) => updateField('state', val)}
                                />
                            </div>
                            <EditableField
                                label="Phone"
                                value={location.phone}
                                onChange={(val) => updateField('phone', val)}
                                type="tel"
                            />
                            <EditableField
                                label="Website"
                                value={location.website}
                                onChange={(val) => updateField('website', val)}
                                type="url"
                            />
                        </div>
                        <div className="mt-4">
                            <EditableField
                                label="Description"
                                value={location.description}
                                onChange={(val) => updateField('description', val)}
                                textarea
                            />
                        </div>
                    </div>

                    {/* Game Info */}
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Game Information</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <EditableField
                                label="Stakes Offered"
                                value={location.stakes_offered}
                                onChange={(val) => updateField('stakes_offered', val)}
                            />
                            <EditableField
                                label="Game Quality"
                                value={location.typical_game_quality}
                                onChange={(val) => updateField('typical_game_quality', val)}
                                options={GAME_QUALITY_OPTIONS}
                            />
                            <EditableField
                                label="Best Days"
                                value={location.best_days}
                                onChange={(val) => updateField('best_days', val)}
                            />
                            <EditableField
                                label="Best Times"
                                value={location.best_times}
                                onChange={(val) => updateField('best_times', val)}
                            />
                            <EditableField
                                label="Max Buy-in"
                                value={location.max_buy_in}
                                onChange={(val) => updateField('max_buy_in', val)}
                                type="number"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                            <div className="flex items-center gap-3">
                                <label className="text-sm text-gray-500 dark:text-gray-400">Bomb Pot</label>
                                <button
                                    onClick={() => updateField('has_bomb_pot', !location.has_bomb_pot)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                                              ${location.has_bomb_pot ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'}`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                                                  ${location.has_bomb_pot ? 'translate-x-6' : 'translate-x-1'}`}
                                    />
                                </button>
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="text-sm text-gray-500 dark:text-gray-400">Straddle</label>
                                <button
                                    onClick={() => updateField('has_straddle', !location.has_straddle)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                                              ${location.has_straddle ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'}`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                                                  ${location.has_straddle ? 'translate-x-6' : 'translate-x-1'}`}
                                    />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Notes */}
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Private Notes</h2>
                        <EditableField
                            label=""
                            value={location.notes}
                            onChange={(val) => updateField('notes', val)}
                            textarea
                        />
                    </div>
                </div>

                {/* Right Column - Top Players & Actions */}
                <div className="space-y-6">
                    {/* Top Players */}
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Players</h2>
                        {location.top_players && location.top_players.length > 0 ? (
                            <div className="space-y-3">
                                {location.top_players.map(player => (
                                    <div
                                        key={player.id}
                                        onClick={() => navigate(`/players/${player.id}`)}
                                        className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-gray-50
                                                 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                                    >
                                        {/* Avatar */}
                                        <div className="relative">
                                            {player.profile_picture ? (
                                                <img
                                                    src={player.profile_picture}
                                                    alt={player.name}
                                                    className="w-10 h-10 rounded-full object-cover"
                                                />
                                            ) : (
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center
                                                              ${PLAYER_CATEGORY_COLORS[player.player_category] || PLAYER_CATEGORY_COLORS.unknown}`}>
                                                    <span className="text-white text-sm font-medium">
                                                        {player.name?.charAt(0)?.toUpperCase() || '?'}
                                                    </span>
                                                </div>
                                            )}
                                            {/* Category dot */}
                                            <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2
                                                          border-white dark:border-gray-800
                                                          ${PLAYER_CATEGORY_COLORS[player.player_category] || PLAYER_CATEGORY_COLORS.unknown}`} />
                                        </div>
                                        {/* Info */}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                {player.name}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                {player.session_count} sessions
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                No player data yet. Track players during sessions to see who frequents this location.
                            </p>
                        )}
                    </div>

                    {/* Quick Links */}
                    {(location.phone || location.website) && (
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Links</h2>
                            <div className="space-y-2">
                                {location.phone && (
                                    <a
                                        href={`tel:${location.phone}`}
                                        className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50
                                                 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        <PhoneIcon className="h-5 w-5 text-primary-600" />
                                        <span className="text-gray-900 dark:text-white">{location.phone}</span>
                                    </a>
                                )}
                                {location.website && (
                                    <a
                                        href={location.website.startsWith('http') ? location.website : `https://${location.website}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50
                                                 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        <GlobeAltIcon className="h-5 w-5 text-primary-600" />
                                        <span className="text-gray-900 dark:text-white truncate">Visit Website</span>
                                    </a>
                                )}
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}

export default LocationDetailPage;
